import store from "@/store";
import { storeActions } from "@/constants/store-actions";
import baseMixin from "@/mixins/base-mixin.js";
import { deepClone } from "@/helpers/object-helper";
import { partNumberStrings } from "@/constants/part-number-strings";
import { applicationConfig } from "@/constants/application-config";
import { queryStrings } from "@/constants/query-strings";
import { getQuerystringParameter } from "@/helpers/querystring-helper";
import { peekQueryFromStash } from "@/router/methods/helpers/querystring-stash";
import { parentAccountNumbers } from "@/constants/insurance";
import { partTypeStrings } from "@/constants/part-type-strings";
import { cartItemTypes } from "@/constants/cart-item-types";

export function getDisplayAmountDue(lineItemsObject, includeTax = true) {
    return getAmountDue(lineItemsObject, includeTax).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
    });
}

export function getAmountDue(lineItemsObject, includeTax = true) {
    let amountDue = 0;
    const order = baseMixin?.methods?.hasSubmittedOrder()
        ? baseMixin?.methods?.getSubmittedOrder()
        : store.getters.order;

    if (lineItemsObject?.glassParts) {
        amountDue += baseMixin.methods.getTotalPriceOfAllLineItemsAndChildParts(
            lineItemsObject.glassParts,
            includeTax
        );
    }

    if (lineItemsObject?.supportingItems) {
        amountDue += baseMixin?.methods?.getTotalPriceOfAllLineItemsAndChildParts(
            lineItemsObject.supportingItems,
            includeTax
        );
    }

    if (store.getters.coverageIsVerified && !order.policy.isNoComp && !order.policy.isItac) {
        amountDue = order.policy.currentDeductible;

        if (order.isMSRFeeApplicable && !order.isMSRFeeCoveredByInsurance) {
            amountDue += getMSRFeePartPrice(lineItemsObject?.supportingItems, includeTax);
        }
    }

    if (lineItemsObject?.vaps) {
        amountDue += baseMixin?.methods?.getTotalPriceOfAllLineItemsAndChildParts(
            lineItemsObject.vaps,
            includeTax
        );
    }

    if (lineItemsObject?.promos) {
        amountDue += baseMixin?.methods?.getTotalPriceOfAllLineItemsAndChildParts(
            lineItemsObject.promos,
            includeTax
        );
    }

    return ((amountDue * 100) / 100).toFixed(2);
}

function getPiaAuthAmountAdjustment() {
    if (applicationConfig.CURRENT_ENVIRONMENT === "Prod") {
        return 0;
    }

    const adjustParam =
        peekQueryFromStash(queryStrings.PIA_AUTH_AMOUNT_ADJUST) ??
        getQuerystringParameter(queryStrings.PIA_AUTH_AMOUNT_ADJUST);

    if (adjustParam == null || adjustParam === "") {
        return 0;
    }

    const adjustment = parseFloat(adjustParam);
    return Number.isFinite(adjustment) ? adjustment : 0;
}

export function getPiaAuthorizationAmount(lineItemsObject) {
    const amountDue = parseFloat(getAmountDue(lineItemsObject));
    const adjustment = getPiaAuthAmountAdjustment();

    if (adjustment === 0) {
        return amountDue.toFixed(2);
    }

    const adjustedAmount = Math.max(0.01, amountDue + adjustment);
    return adjustedAmount.toFixed(2);
}

export function getSubTotal(lineItemsObject) {
    // this is amountDue without sales tax
    return getAmountDue(lineItemsObject, false);
}

export function getSalesTax(lineItemsObject) {
    // this is amountDue minus subTotal
    return (((getAmountDue(lineItemsObject) - getSubTotal(lineItemsObject)) * 100) / 100).toFixed(
        2
    );
}

export function getAmountDueWithDonation(lineItemsObject, donationLineItem) {
    // this is amount due with Donation added
    const lineItemsCloneWithDonation = deepClone(lineItemsObject);
    lineItemsCloneWithDonation.supportingItems.push(donationLineItem);
    return getAmountDue(lineItemsCloneWithDonation);
}

export async function getPricingByDayPartWithPrice(pageNameToLog) {
    // Get the Pricing By Day Part
    const basePriceByDayPart = await baseMixin.methods.dispatchStoreActionWithLogging(
        storeActions.GET_PRICING_BY_DAY_PART,
        null,
        pageNameToLog,
        false
    );

    if (
        basePriceByDayPart?.data === null ||
        basePriceByDayPart?.data === undefined ||
        basePriceByDayPart?.data === ""
    ) {
        return null;
    }

    // Get the Pricing By Day Base Part price
    const pricingResults = await baseMixin.methods.dispatchStoreActionWithLogging(
        storeActions.PRICE_ORDER_ITEMS_AND_SAVE_SERVER_DATA,
        {
            availableLineItems: [basePriceByDayPart?.data],
        },
        pageNameToLog,
        false
    );

    return pricingResults[0];
}

export function getMSRFeePartPrice(supportingItems, includeTax) {
    let msrFeePrice = 0;
    const msrFeeLineItem = supportingItems?.find(
        (lineItem) =>
            lineItem.partNumber === partNumberStrings.MOBILE_STATIC_RECAL_FEE ||
            lineItem.partNumber === partNumberStrings.MOBILE_DUAL_RECAL_FEE
    );
    if (msrFeeLineItem) {
        msrFeePrice = baseMixin?.methods?.getTotalPriceOfAllLineItemsAndChildParts(
            [msrFeeLineItem],
            includeTax
        );
    }
    return msrFeePrice;
}

/**
 * True when sellingPrice is present and the customer total is $0.
 * Unpriced heritage parts also serialize as 0, so do not use alone.
 */
function isExplicitlyZeroPriced(lineItem) {
    if (lineItem?.sellingPrice == null) {
        return false;
    }

    return (
        Number(lineItem.sellingPrice) +
            Number(lineItem.kitPrice ?? 0) +
            Number(lineItem.laborAmount ?? 0) ===
        0
    );
}

function isWiperLineItem(lineItem) {
    const partType = (lineItem?.partType ?? "").toUpperCase();
    const cartItemType = (lineItem?.cartItemType ?? "").toUpperCase();

    return (
        partType.includes(partTypeStrings.FRONT_WIPER) ||
        partType.includes(partTypeStrings.REAR_WIPER) ||
        cartItemType === cartItemTypes.FRONT_WIPERS ||
        cartItemType === cartItemTypes.REAR_WIPERS
    );
}

/**
 * Erie verified-insurance free wipers (ClientWiperPromo), not merely unpriced $0 items.
 * Matches CoverageStatementPresenter: verified + Erie + wiper + $0.
 */
export function isClientPromoFreeWiper(lineItem, { parentAccountNumber, coverageIsVerified } = {}) {
    return (
        !!coverageIsVerified &&
        Number(parentAccountNumber) === parentAccountNumbers.ERIE &&
        isWiperLineItem(lineItem) &&
        isExplicitlyZeroPriced(lineItem)
    );
}

/** Part numbers for Erie free wipers on the order (includes child parts). */
export function getClientPromoFreeWiperPartNumbers(lineItems, eligibilityContext) {
    const partNumbers = new Set();

    const walk = (items) => {
        items?.forEach((item) => {
            if (isClientPromoFreeWiper(item, eligibilityContext) && item.partNumber) {
                partNumbers.add(item.partNumber);
            }
            if (item.childParts?.length) {
                walk(item.childParts);
            }
        });
    };

    walk(lineItems);
    return partNumbers;
}

export function addPricesToLineItems(lineItems, pricingLineItems, options = {}) {
    const { shouldPreservePrice } = options;

    lineItems.forEach((lineItem) => {
        const lineItemIndex = pricingLineItems.findIndex(
            (pricingLineItem) => pricingLineItem.partNumber === lineItem.partNumber
        );

        if (lineItem.childParts) {
            addPricesToLineItems(lineItem.childParts, pricingLineItems, options);
        }

        if (lineItemIndex === -1) {
            return;
        }

        const pricedLineItem = pricingLineItems.splice(lineItemIndex, 1)[0];

        // CASH-3056: keep / force client-promo free-wiper prices at $0 instead of catalog cost.
        if (shouldPreservePrice?.(lineItem)) {
            lineItem.laborAmount = 0;
            lineItem.sellingPrice = 0;
            lineItem.kitPrice = 0;
            lineItem.salesTax = 0;
            return;
        }

        lineItem.laborAmount = pricedLineItem.laborAmount;
        lineItem.sellingPrice = pricedLineItem.sellingPrice;
        lineItem.kitPrice = pricedLineItem.kitPrice;
        lineItem.salesTax = pricedLineItem.salesTax;
    });

    return lineItems;
}
